import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Typography,
  Row,
  Col,
  Space,
  Grid,
} from "antd";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const theme = {
  primary: "var(--theme1, #eca413)",
  primaryDark: "var(--theme1Dark, #132272)",
  muted: "rgba(0, 0, 0, 0.45)",
  border: "rgba(19, 34, 114, 0.12)",
};

const AddAgent = () => {
  const { request } = useRequest();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const screens = useBreakpoint();

  const [loading, setLoading] = useState(false);
  const [hotels, setHotels] = useState([]);
  const authRole = localStorage.getItem("authRole") || "admin";

  const pagePad = screens.xs ? 16 : screens.sm ? 24 : 32;

  const inputStyle = {
    borderRadius: 10,
    height: 48,
    fontSize: 15,
    border: `1px solid ${theme.border}`,
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  };

  const inputPrefixStyle = {
    color: theme.muted,
    fontSize: 16,
  };

  const labelStyle = {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 6,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    color: "rgba(0, 0, 0, 0.85)",
  };

  const getManagerId = () => {
    try {
      const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
      const loginData = JSON.parse(localStorage.getItem("loginData") || "{}");
      return profile?.Hotel_Id || loginData?.managerDetails?.Hotel_Id || null;
    } catch (_err) {
      return null;
    }
  };

  useEffect(() => {
    if (authRole !== "hotel") {
      ShowToast("Admin has view-only access for agents", Severty.WARNING);
      navigate("/agent-management", { replace: true });
      return;
    }

    const managerId = getManagerId();
    if (!managerId) {
      ShowToast("Manager session not found. Please login again.", Severty.ERROR);
      navigate("/login", { replace: true });
      return;
    }

    request({
      url: `${apiPath.baseURL}/get_Hotels_of_HotelPartner/${managerId}`,
      method: "GET",
      onSuccess: (res) => {
        if (res?.success) {
          setHotels(
            res.Hotels_details ||
            res.Hotel_Details ||
            res.hotel_Details ||
            res.data ||
            []
          );
        }
      },
      onError: () => {
        setHotels([]);
      },
    });
  }, [authRole, navigate]);

  const onFinish = (values) => {
    const managerId = getManagerId();

    if (!managerId) {
      ShowToast("Session expired, login again", Severty.ERROR);
      return;
    }

    const autoAssociatedHotels = hotels
      .map((hotel) => hotel.Hotel_Id)
      .filter(Boolean);

    if (!autoAssociatedHotels.length) {
      ShowToast("No hotels are assigned to this manager", Severty.ERROR);
      return;
    }

    setLoading(true);

    request({
      url: `${apiPath.baseURL}/manager/create_agent/${managerId}`,
      method: "POST",
      data: {
        ...values,
        associated_hotel_ids: autoAssociatedHotels,
      },
      onSuccess: (res) => {
        setLoading(false);
        if (res?.success) {
          ShowToast(res?.message || "Agent created successfully", Severty.SUCCESS);
          form.resetFields();
          navigate("/agent-management");
        } else {
          ShowToast(res?.message || "Failed", Severty.ERROR);
        }
      },
      onError: () => {
        setLoading(false);
        ShowToast("Something went wrong", Severty.ERROR);
      },
    });
  };

  return (
    <div
      className="add-agent-page"
      style={{
        minHeight: "100%",
      }}
    >
      <Card
      >
        {/* HEADER */}
        <div style={{ marginBottom: 24 }}>
          <Button
          type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/agent-management")}
            style={{ padding: 0  , height: "30px"}}
          >
            Back
          </Button>

          <Title level={3} style={{ marginTop: 8 }}>
            Add New Agent
          </Title>
          <Text type="secondary">Create a new agent</Text>
        </div>

        {/* FORM */}
        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
        >
          {/* ✅ FIXED GAP HERE */}
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Agent Name"
                name="agent_name"
                rules={[{ required: true, message: "Enter agent name" }]}
              >
                <Input size="large" placeholder="Enter agent name" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: "Enter email" },
                  { type: "email", message: "Invalid email" },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Enter email"
                  autoComplete="new-email"
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Password"
                name="password"
                rules={[{ required: true, message: "Enter password" },
                  { min: 8, message: "Password must be at least 8 characters" }
                ]}
              >
                <Input.Password
                  size="large"
                  placeholder="Enter password"
                  autoComplete="new-password"
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Phone Number"
                name="phone_no"
                rules={[
                  { required: true, message: "Enter phone number" },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                
                      const cleaned = value.replace(/[^\d+]/g, "");
                      const regex = /^\+[1-9]\d{7,14}$/;
                
                      if (!regex.test(cleaned)) {
                        return Promise.reject(
                          "Enter a valid phone number with country code (e.g. +1234567890)"
                        );
                      }
                
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <PhoneInput
                  country={'in'}
                  value={form.getFieldValue('phone_no')}
                  onChange={(phone, country) => {
                    const dialCode = country?.dialCode || "";
                    const numberOnly = phone.startsWith(dialCode) ? phone.slice(dialCode.length) : phone;
                    form.setFieldsValue({ phone_no: `+${dialCode} ${numberOnly}` });
                  }}
                  inputStyle={{
                    width: '100%',
                    fontSize: '16px'
                  }}
                  dropdownStyle={{
                    fontSize: '14px'
                  }}
                  placeholder="Enter phone number"
                  enableSearch={true}
                  disableSearchIcon={false}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Notes"
                name="notes"
              >
                <Input.TextArea rows={3} placeholder="Optional notes" />
              </Form.Item>
            </Col>
          </Row>

            <div
              style={{
                marginTop: 28,
                paddingTop: 24,
                borderTop: `1px solid ${theme.border}`,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Space
                direction={screens.xs ? "vertical" : "horizontal"}
                style={{ width: screens.xs ? "100%" : "auto" }}
                size={12}
              >
                <Button
                  size="large"
                  onClick={() => navigate("/agent-management")}
                  style={{
                    width: screens.xs ? "100%" : 128,
                    height: 48,
                    borderRadius: 10,
                    fontWeight: 600,
                    borderColor: theme.border,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  style={{
                    width: screens.xs ? "100%" : "auto",
                    minWidth: 200,
                    height: 48,
                    borderRadius: 10,
                    fontWeight: 600,
                    background: theme.primary,
                    borderColor: theme.primary,
                    boxShadow: "0 8px 24px rgba(53, 80, 220, 0.28)",
                  }}
                >
                  Create agent
                </Button>
              </Space>
            </div>
          </Form>
        </Card>

      <style>{`
        .add-agent-page .ant-card {
          position: relative;
        }
        .add-agent-page .ant-input,
        .add-agent-page .ant-input-password,
        .add-agent-page .ant-input-affix-wrapper {
          border-radius: 10px !important;
          height: 48px !important;
          border: 1px solid ${theme.border} !important;
          transition: border-color 0.2s ease, box-shadow 0.2s ease !important;
          background-color: #fff !important;
          overflow: hidden;
          display: flex;
          align-items: center;
        }
        .add-agent-page .ant-input-affix-wrapper {
          padding-left: 12px !important;
        }
        .add-agent-page .ant-input-password {
          padding: 0 !important;
          padding-left: 12px !important;
        }
        .add-agent-page .ant-input-password .ant-input {
          border: none !important;
          height: 46px !important;
          box-shadow: none !important;
        }
        .add-agent-page .ant-input-affix-wrapper .ant-input {
          height: 46px !important;
          border: none !important;
          padding-left: 10px !important;
        }
        .add-agent-page .ant-input-affix-wrapper:hover,
        .add-agent-page .ant-input:hover {
          border-color: rgba(53, 80, 220, 0.45) !important;
        }
        .add-agent-page .ant-input-affix-wrapper-focused,
        .add-agent-page .ant-input:focus {
          border-color: var(--theme1, #eca413) !important;
        }
        .add-agent-page .ant-input-password-icon {
          color: rgba(0,0,0,0.35);
        }
        .add-agent-page .ant-input-password-icon:hover {
          color: var(--theme1, #eca413);
        }
        .add-agent-page .add-agent-select.ant-select .ant-select-selector {
          border-radius: 10px !important;
          min-height: 48px !important;
          padding-top: 8px !important;
          padding-bottom: 8px !important;
          border: 1px solid ${theme.border} !important;
        }
        .add-agent-page .add-agent-select.ant-select:hover .ant-select-selector {
          border-color: rgba(53, 80, 220, 0.45) !important;
        }
        .add-agent-page .add-agent-select.ant-select-focused .ant-select-selector {
          border-color: var(--theme1, #eca413) !important;
          box-shadow: 0 0 0 3px rgba(53, 80, 220, 0.15) !important;
        }
        .add-agent-page textarea.ant-input {
          border: 1px solid ${theme.border} !important;
        }
        .add-agent-page textarea.ant-input:hover {
          border-color: rgba(53, 80, 220, 0.45) !important;
        }
        .add-agent-page textarea.ant-input:focus {
          border-color: var(--theme1, #eca413) !important;
          box-shadow: 0 0 0 3px rgba(53, 80, 220, 0.15) !important;
        }
        .add-agent-page .ant-form-item {
          margin-bottom: 0 !important;
        }
        .add-agent-page .ant-form-item-label {
          padding-bottom: 6px !important;
        }
        .add-agent-page .ant-btn-primary:hover {
          background: var(--theme1Dark, #132272) !important;
          border-color: var(--theme1Dark, #132272) !important;
        }
        @media (max-width: 576px) {
          .add-agent-page .ant-input,
          .add-agent-page .ant-input-password,
          .add-agent-page .ant-input-affix-wrapper {
            height: 44px !important;
          }
          .add-agent-page .add-agent-select.ant-select .ant-select-selector {
            min-height: 44px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AddAgent;
